// The strike ladder's published rules (policy §4; moderation phase 2 arc 5,
// docs/plans/MODERATION_PHASE_2_PLAN.md decisions 1–4, 9 and 16).
//
// CommonJS, like moderationCategories.js, so the server applies exactly the
// numbers the "How enforcement works" page prints.
//
// LADDER_LIVE_SINCE is the switch (decision 16, Kurtis 2026-10-12 Q7): null
// keeps the ladder dormant — no strike, no automatic account action, no
// "strikes until the next step" and no help page. Going live is the commit
// that sets it to the UTC moment the help page is published, together with
// that page. Only decisions taken on or after it count, the "first Medium"
// rule included: the ladder is never applied to anything decided before it
// was published (DSA Art 23).

/** SQLite UTC datetime text ("YYYY-MM-DD HH:MM:SS"), or null while dormant. */
const LADDER_LIVE_SINCE = null;

/** How long a strike lasts, by the decision category's severity (decision 2). */
const STRIKE_DAYS = Object.freeze({ medium: 90, high: 365, critical: 365 });

/**
 * What the Nth active strike does (decision 3). Every step from the third on
 * keeps the suspension and lists the account for a termination decision by a
 * person (Kurtis 2026-10-12 Q4): termination is never automatic.
 */
const STEPS = Object.freeze([
  Object.freeze({ strikes: 1, action: 'restricted', days: 7 }),
  Object.freeze({ strikes: 2, action: 'suspended', days: 30 }),
  Object.freeze({ strikes: 3, action: 'suspended', days: 30, proposeTermination: true })
]);

/** "First Medium" looks back this far for an earlier Medium-or-higher decision (decision 1). */
const FIRST_MEDIUM_LOOKBACK_MONTHS = 12;

/** The public page that states these rules (decision 16). */
const HELP_PATH = '/help/enforcement';

function liveSinceDate(since = LADDER_LIVE_SINCE) {
  if (!since) return null;
  const s = String(since);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Reports made in bad faith (policy §6.5; moderation phase 2 arc 6, Kurtis
 * 2026-10-12). Never automatic: these numbers only list a reporter for an
 * admin, who warns first and may then pause their reports.
 *   - listed at MIN_CLOSED or more closed reports filed in WINDOW_DAYS, of
 *     which UNFOUNDED_SHARE or more were dismissed with nothing decided on
 *     the content since, or at PARENT_REQUESTS or more such dismissed "this
 *     is my child" requests (each one hid somebody's photo before review);
 *   - a warning stands for WARNING_MONTHS (a pause resting on it renews it);
 *   - a pause lasts PAUSE_DAYS, and only once the ladder is published (the
 *     rule is stated on the help page before it is applied: DSA Art 23(4)).
 */
const REPORTER_ABUSE = Object.freeze({
  WINDOW_DAYS: 90,
  MIN_CLOSED: 5,
  UNFOUNDED_SHARE: 0.8,
  PARENT_REQUESTS: 2,
  WARNING_MONTHS: 12,
  PAUSE_DAYS: 30
});

/** Whether the ladder is published and running now. */
function isLadderLive(now = Date.now(), since = LADDER_LIVE_SINCE) {
  const d = liveSinceDate(since);
  return !!d && now >= d.getTime();
}

/** The step N active strikes reach (N ≥ 1), or null for none. */
function stepFor(activeStrikes) {
  if (!activeStrikes || activeStrikes < 1) return null;
  return STEPS[Math.min(activeStrikes, STEPS.length) - 1];
}

module.exports = {
  LADDER_LIVE_SINCE,
  STRIKE_DAYS,
  STEPS,
  FIRST_MEDIUM_LOOKBACK_MONTHS,
  REPORTER_ABUSE,
  HELP_PATH,
  liveSinceDate,
  isLadderLive,
  stepFor
};
